import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { FloatingSection } from '@uhg-abyss/web/ui/FloatingSection';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import React from 'react';

import { useWizardContext } from '../../../../context';

/**
 * Footer
 *
 * Provides a floating navigation bar for users to move forwards or backwards between steps at the bottom of the
 * wizard/stepper interface.
 *
 * @param form
 * @param handleSubmit
 * @param isCurrentStepValid
 * @param ref
 * @returns {Element}
 * @constructor
 */
export function Footer({ form = {}, handleSubmit = () => {}, isCurrentStepValid = true, ref = {} }) {
  const wizardContext = useWizardContext();

  const { action, currentStep, firstStep, id, lastStep, mode, nextStep, previousStep, status } = wizardContext;

  const router = useRouter();

  return (
    <ErrorHandler location="src/features/ActionPaths/Wizard/components/misc/Layout/Footer/Footer.jsx">
      <FloatingSection
        containerRef={ref}
        css={{
          'abyss-floating-section-root': {
            bottom: '0px',
            boxShadow: 'none !important',
            padding: `var(--abyss-space-xs) 0px !important`,
            zIndex: 999,
          },
          boxShadow: 'none !important',
        }}
        position="bottom"
        space="1"
      >
        <Grid
          css={{
            margin: '0px !important',
            padding: `${themeConfiguration?.theme?.space?.xl}px 0px !important`,
          }}
        >
          <Grid.Col
            css={{ paddingLeft: 0, paddingRight: 0 }}
            span={{
              lg: '50%',
              md: '50%',
              xs: '50%',
            }}
          >
            <Layout.Group alignLayout="left">
              {currentStep?.order === firstStep?.order ? (
                <Button
                  onClick={() => {
                    handleSubmit();
                    router?.navigate('/action-paths');
                  }}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  before={<IconSymbol icon="chevron_left" />}
                  onClick={() => {
                    handleSubmit();
                    router?.navigate(`/action-paths/${id}/${action}/${mode}/${status}/step/${previousStep?.order}`);
                  }}
                  type="button"
                  variant="outline"
                >
                  {previousStep?.label || ''}
                </Button>
              )}
            </Layout.Group>
          </Grid.Col>
          <Grid.Col
            css={{ paddingLeft: 0, paddingRight: 0 }}
            span={{
              lg: '50%',
              md: '50%',
              xs: '50%',
            }}
          >
            <Layout.Group alignLayout="right">
              {currentStep?.order === lastStep?.order ? (
                <Button
                  after={<IconSymbol css={{ color: '#fff' }} icon="keyboard_double_arrow_right" />}
                  disabled={!isCurrentStepValid}
                  onClick={() => {
                    handleSubmit();
                    router?.navigate(`/action-paths?highlight=${form?.getValues('id')}`);
                  }}
                  type="button"
                  variant="solid"
                >
                  Save Action Path
                </Button>
              ) : (
                <Button
                  after={<IconSymbol css={{ color: '#fff' }} icon="chevron_right" />}
                  disabled={!isCurrentStepValid}
                  onClick={() => {
                    if (isCurrentStepValid) {
                      handleSubmit();
                      if (action === 'edit') {
                        router?.navigate(`/action-paths/${id}/${action}/${mode}/${status}/step/${nextStep?.order}`);
                      }
                    }
                  }}
                  type="button"
                  variant="solid"
                >
                  {nextStep?.label || ''}
                </Button>
              )}
            </Layout.Group>
          </Grid.Col>
        </Grid>
      </FloatingSection>
    </ErrorHandler>
  );
}
